// ============================================================================
// ENVIRONMENT CONFIGURATION
// ============================================================================
// Centralized configuration for all environment variables
// ============================================================================

// ============================================================================
// TYPES
// ============================================================================

export interface EnvironmentConfig {
  // API Configuration
  api: {
    baseUrl: string;
    version: number;
    timeout: number;
    cacheTtl: number;
    refetchOnFocus: boolean;
    refetchOnReconnect: boolean;
    keepUnusedDataFor: number;
    appClientId: string;
  };

  // MSAL Authentication
  msal: {
    clientId: string;
    authority: string;
    authorityDomain: string;
    redirectUri: string;
    postLogoutRedirectUri: string;
    cacheLocation: "sessionStorage" | "localStorage";
    storeAuthStateInCookie: boolean;
    scopes: string[];
    tokenScopes: string[];
  };

  // Application Settings
  app: {
    env: string;
    version: string;
    name: string;
    description: string;
    appClientId: string;
    /** Optional absolute URL for `sendBeacon` / POST JSON when PWA install completes. */
    pwaInstallReportUrl: string;
  };

  // Maps
  maps: {
    apiKey: string;
  };

  // Feature Flags
  features: {
    realTimeNotifications: boolean;
    offlineSupport: boolean;
    analytics: boolean;
    debugMode: boolean;
  };

  // Development & Debugging
  debug: {
    enabled: boolean;
    logLevel: "error" | "warn" | "info" | "debug";
    reduxDevTools: boolean;
    msalDebug: boolean;
  };

  // Performance & Caching
  performance: {
    cdnUrl?: string;
  };

  /**
   * External services.
   *
   * URLs only, and deliberately so. Every VITE_ value is compiled into the JavaScript
   * bundle and readable by anyone who opens the app, so a slot named for a key is an
   * invitation to publish one. Credentials for a payment or email provider belong to the
   * API, which can hold them server-side.
   */
  services: {
    graphApiEndpoint: string;
  };

  // Security
  security: {
    cspNonce?: string;
    securityHeaders: boolean;
    rateLimit: {
      enabled: boolean;
      maxRequests: number;
      windowMs: number;
    };
  };

  // Deployment
  deployment: {
    buildTime?: string;
    gitCommitHash?: string;
    env: string;
  };

  // Localization
  localization: {
    defaultLocale: string;
    supportedLocales: string[];
  };

  // Notifications
  notifications: {
    websocketUrl: string;
    pushNotifications: boolean;
    vapidKey?: string;
  };

  // File Upload
  fileUpload: {
    maxSize: number;
    allowedTypes: string[];
  };

  // Testing
  testing: {
    testMode: boolean;
    mockApi: boolean;
  };
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function getEnvVar(key: string, defaultValue?: string): string {
  const value = import.meta.env[key];
  if (value === undefined && defaultValue === undefined) {
    console.warn(`Environment variable ${key} is not set`);
    return "";
  }
  return value || defaultValue || "";
}

function getEnvVarAsBoolean(
  key: string,
  defaultValue: boolean = false
): boolean {
  const value = getEnvVar(key);
  if (value === "") return defaultValue;
  return value.toLowerCase() === "true";
}

function getEnvVarAsNumber(key: string, defaultValue: number): number {
  const value = getEnvVar(key);
  if (value === "") return defaultValue;
  const num = parseInt(value, 10);
  return isNaN(num) ? defaultValue : num;
}

function getEnvVarAsArray(key: string, defaultValue: string[] = []): string[] {
  const value = getEnvVar(key);
  if (value === "") return defaultValue;
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

// ============================================================================
// CONFIGURATION
// ============================================================================

export const config: EnvironmentConfig = {
  // API Configuration
  api: {
    baseUrl: getEnvVar("VITE_API_BASE_URL"),
    version: getEnvVarAsNumber("VITE_API_VERSION", 1),
    timeout: getEnvVarAsNumber("VITE_API_TIMEOUT", 30000),
    cacheTtl: getEnvVarAsNumber("VITE_API_CACHE_TTL", 300),
    refetchOnFocus: getEnvVarAsBoolean("VITE_API_REFETCH_ON_FOCUS", false),
    refetchOnReconnect: getEnvVarAsBoolean(
      "VITE_API_REFETCH_ON_RECONNECT",
      true
    ),
    keepUnusedDataFor: getEnvVarAsNumber("VITE_API_KEEP_UNUSED_DATA_FOR", 300),
    appClientId: getEnvVar("VITE_API_CLIENT_ID")
  },

  // MSAL Authentication
  msal: {
    clientId: getEnvVar("VITE_MSAL_CLIENT_ID"),
    authority: getEnvVar("VITE_MSAL_AUTHORITY"),
    authorityDomain: getEnvVar("VITE_MSAL_AUTHORITY_DOMAIN"),
    redirectUri: getEnvVar(
      "VITE_MSAL_REDIRECT_URI",
      typeof window !== "undefined"
        ? `${window.location.origin}/login`
        : undefined
    ),
    postLogoutRedirectUri: getEnvVar(
      "VITE_MSAL_POST_LOGOUT_REDIRECT_URI",
      typeof window !== "undefined"
        ? `${window.location.origin}/logout`
        : undefined
    ),
    cacheLocation: ((): "sessionStorage" | "localStorage" => {
      const isProductionRuntime =
        import.meta.env.PROD ||
        getEnvVar("VITE_APP_ENV", "production") === "production" ||
        getEnvVar("VITE_APP_ENV", "") === "staging";
      if (isProductionRuntime) {
        return "sessionStorage";
      }
      return getEnvVar("VITE_MSAL_CACHE_LOCATION", "localStorage") as
        | "sessionStorage"
        | "localStorage";
    })(),
    storeAuthStateInCookie: getEnvVarAsBoolean(
      "VITE_MSAL_STORE_AUTH_STATE_IN_COOKIE",
      true
    ),
    scopes: getEnvVarAsArray("VITE_MSAL_SCOPES", [
      "User.Read",
      "openid",
      "profile",
      "email",
      "offline_access"
    ]),
    tokenScopes: getEnvVarAsArray("VITE_MSAL_TOKEN_SCOPES", [
      "openid",
      "profile",
      "email",
      "offline_access",
      "api://641bda8c-8472-4261-b8d8-2eee3b3f94b4/access_as_user"
    ])
  },

  // Application Settings
  app: {
    env: getEnvVar("VITE_APP_ENV", "production"),
    version: getEnvVar("VITE_APP_VERSION", "0.2.1"),
    name: getEnvVar("VITE_APP_NAME", "Tempii"),
    description: getEnvVar(
      "VITE_APP_DESCRIPTION",
      "Healthcare Staffing Platform"
    ),
    appClientId: getEnvVar(
      "VITE_APP_CLIENT_ID",
      "502ac024-f1bc-4d6c-ace3-141ae7927e52"
    ),
    pwaInstallReportUrl: getEnvVar("VITE_PWA_INSTALL_REPORT_URL", "")
  },

  // Maps
  maps: {
    apiKey: getEnvVar("VITE_APP_GOOGLE_MAPS_API_KEY")
  },

  // Feature Flags
  features: {
    realTimeNotifications: getEnvVarAsBoolean(
      "VITE_FEATURE_REAL_TIME_NOTIFICATIONS",
      true
    ),
    offlineSupport: getEnvVarAsBoolean("VITE_FEATURE_OFFLINE_SUPPORT", true),
    analytics: getEnvVarAsBoolean("VITE_FEATURE_ANALYTICS", true),
    debugMode:
      import.meta.env.PROD || getEnvVar("VITE_APP_ENV", "production") === "production"
        ? false
        : getEnvVarAsBoolean("VITE_FEATURE_DEBUG_MODE", false)
  },

  // Development & Debugging
  debug: (() => {
    const isProductionRuntime =
      import.meta.env.PROD || getEnvVar("VITE_APP_ENV", "production") === "production";
    const requestedDebug = getEnvVarAsBoolean(
      "VITE_DEBUG_ENABLED",
      import.meta.env.DEV
    );
    return {
      enabled: isProductionRuntime ? false : requestedDebug,
      logLevel: (isProductionRuntime
        ? "error"
        : getEnvVar("VITE_LOG_LEVEL", "error")) as
        | "error"
        | "warn"
        | "info"
        | "debug",
      reduxDevTools: isProductionRuntime
        ? false
        : getEnvVarAsBoolean("VITE_REDUX_DEVTOOLS_ENABLED", false),
      msalDebug: isProductionRuntime
        ? false
        : getEnvVarAsBoolean("VITE_MSAL_DEBUG_ENABLED", import.meta.env.DEV)
    };
  })(),

  // Performance & Caching
  performance: {
    cdnUrl: getEnvVar("VITE_CDN_URL")
  },

  // External Services
  services: {
    graphApiEndpoint: getEnvVar(
      "VITE_GRAPH_API_ENDPOINT",
      "https://graph.microsoft.com/v1.0"
    )
  },

  // Security
  security: {
    cspNonce: getEnvVar("VITE_CSP_NONCE"),
    securityHeaders: getEnvVarAsBoolean("VITE_SECURITY_HEADERS_ENABLED", true),
    rateLimit: {
      enabled: getEnvVarAsBoolean("VITE_RATE_LIMIT_ENABLED", true),
      maxRequests: getEnvVarAsNumber("VITE_RATE_LIMIT_MAX_REQUESTS", 100),
      windowMs: getEnvVarAsNumber("VITE_RATE_LIMIT_WINDOW_MS", 900000)
    }
  },

  // Deployment
  deployment: {
    buildTime: getEnvVar("VITE_BUILD_TIME"),
    gitCommitHash: getEnvVar("VITE_GIT_COMMIT_HASH"),
    env: getEnvVar("VITE_DEPLOYMENT_ENV", "production")
  },

  // Localization
  localization: {
    defaultLocale: getEnvVar("VITE_DEFAULT_LOCALE", "en"),
    supportedLocales: getEnvVarAsArray("VITE_SUPPORTED_LOCALES", [
      "en",
      "es",
      "fr"
    ])
  },

  // Notifications
  notifications: {
    websocketUrl: getEnvVar("VITE_WEBSOCKET_URL", "wss://api.tempii.ai/ws"),
    pushNotifications: getEnvVarAsBoolean(
      "VITE_PUSH_NOTIFICATION_ENABLED",
      true
    ),
    vapidKey: getEnvVar("VITE_PUSH_NOTIFICATION_VAPID_KEY")
  },

  // File Upload
  fileUpload: {
    maxSize: getEnvVarAsNumber("VITE_FILE_UPLOAD_MAX_SIZE", 10485760), // 10MB
    allowedTypes: getEnvVarAsArray("VITE_FILE_UPLOAD_ALLOWED_TYPES", [
      "image/jpeg",
      "image/png",
      "image/gif",
      "application/pdf"
    ])
  },

  // Testing
  testing: {
    testMode: getEnvVarAsBoolean("VITE_TEST_MODE", false),
    mockApi: getEnvVarAsBoolean("VITE_MOCK_API_ENABLED", false)
  }
};

// ============================================================================
// VALIDATION
// ============================================================================

// function looksLikeDevelopmentHost(value: string): boolean {
//   const normalized = value.toLowerCase();
//   return (
//     normalized.includes("-dev") ||
//     normalized.includes("web-dev.tempii.ai") ||
//     normalized.includes("localhost") ||
//     normalized.includes("127.0.0.1")
//   );
// }

function validateConfig(): void {
  const errors: string[] = [];
  const isProductionRuntime =
    import.meta.env.PROD || config.app.env === "production";

  // Required fields validation
  if (!config.msal.clientId) {
    errors.push("VITE_MSAL_CLIENT_ID is required");
  }

  if (!config.msal.authority) {
    errors.push("VITE_MSAL_AUTHORITY is required");
  }

  if (!config.msal.authorityDomain) {
    errors.push("VITE_MSAL_AUTHORITY_DOMAIN is required");
  }

  if (!config.api.baseUrl) {
    errors.push("VITE_API_BASE_URL is required");
  }

  // URL validation
  if (config.api.baseUrl) {
    try {
      new URL(config.api.baseUrl);
    } catch {
      errors.push("VITE_API_BASE_URL must be a valid URL");
    }
  }

  if (config.msal.authority) {
    try {
      new URL(config.msal.authority);
    } catch {
      errors.push("VITE_MSAL_AUTHORITY must be a valid URL");
    }
  }

  // if (isProductionRuntime) {
  //   if (looksLikeDevelopmentHost(config.api.baseUrl)) {
  //     errors.push(
  //       "Production builds must not use a development API host (VITE_API_BASE_URL)"
  //     );
  //   }
  //   if (looksLikeDevelopmentHost(config.msal.redirectUri)) {
  //     errors.push(
  //       "Production builds must not use a development MSAL redirect URI"
  //     );
  //   }
  //   if (looksLikeDevelopmentHost(config.msal.postLogoutRedirectUri)) {
  //     errors.push(
  //       "Production builds must not use a development MSAL logout redirect URI"
  //     );
  //   }
  // }

  // Log validation errors
  if (errors.length > 0) {
    console.error("Environment configuration validation failed:");
    errors.forEach((error) => console.error(`- ${error}`));

    if (isProductionRuntime) {
      throw new Error("Environment configuration validation failed");
    }
  }
}

// Always validate; production throws on invalid/dev hosts.
validateConfig();

// ============================================================================
// EXPORTS
// ============================================================================

export default config;

// Convenience exports for commonly used configs
export const { api, msal, app, features, debug, performance } = config;
